/**
 * A member's sets (co-ords) on the Wardrobe tab: every set in any state, making one from the
 * garments ticked in the table, and retiring one.
 *
 * Approving or rejecting a set is deliberately absent. Whether pieces are a set the member wears
 * together is the member's own judgement, made in the app, so this panel only reports it.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  createMemberGarmentSetApiV1InternalUsersIdGarmentSetsPost,
  memberGarmentSetsApiV1InternalUsersIdGarmentSetsGet,
  retireMemberGarmentSetApiV1InternalUsersIdGarmentSetsSetIdDelete,
  type AdminGarmentSet,
} from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText, Section } from '@ui/console/primitives';
import { IdentifierValue, formatCount } from '@ui/console/measures';
import { ActionDialog } from '@ui/console/PageShell';
import { TextAreaField, TextField } from '@ui/console/forms';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Actions, DetailList, Status } from './components';
import { errorDetail } from './operation-policy';
import { EntityRef } from './references';

export const MIN_SET_PIECES = 2;
export const MAX_SET_PIECES = 8;

export const SET_DEFINITION =
  'A set, or co-ord, is the garments that together make one conventional outfit, such as a matching dhoti and kurta, or a suit jacket with its matching trousers.';

const APPROVAL_WORDS: Record<AdminGarmentSet['approval_status'], string> = {
  pending: 'Waiting for the member',
  auto_approved: 'Approved automatically',
  member_approved: 'Approved by the member',
  rejected: 'The member said this is not a set',
};

function when(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : 'Not recorded';
}

function madeBy(set: AdminGarmentSet) {
  if (set.source === 'detector')
    return `The set detector (${set.detector_version ?? 'unknown version'})`;
  if (set.created_by_id) return <EntityRef kind="staff" id={set.created_by_id} />;
  return 'An operator';
}

type Load =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'failed'; detail: string }
  | { status: 'ready'; sets: AdminGarmentSet[] };

/** The Sets section. `revision` changes whenever a set is made elsewhere on the page. */
export function MemberGarmentSets({ memberId, revision }: { memberId: string; revision: number }) {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const refresh = useCallback(async () => {
    configureAdminApi();
    const result = await memberGarmentSetsApiV1InternalUsersIdGarmentSetsGet({
      path: { id: memberId },
    });
    handleUnauthorized(result.response.status);
    if (result.response.status === 403) {
      setLoad({ status: 'forbidden' });
      return;
    }
    if (result.error || !result.data) {
      setLoad({ status: 'failed', detail: errorDetail(result.error, 'request_failed') });
      return;
    }
    setLoad({ status: 'ready', sets: result.data.sets });
  }, [memberId]);
  useEffect(() => void refresh(), [refresh, revision]);

  return (
    <Section
      title="Sets"
      description={`${SET_DEFINITION} The member sees these on the Sets tab of their wardrobe and approves or rejects each one there. To make a set, tick its garments in the table view above.`}
    >
      {load.status === 'loading' ? (
        <p className="py-4 text-sm text-tertiary" aria-live="polite">
          Loading…
        </p>
      ) : load.status === 'forbidden' ? (
        <Callout tone="warning" title="Your role cannot read this member’s sets.">
          This is a permission boundary, not an empty record.
        </Callout>
      ) : load.status === 'failed' ? (
        <Callout
          tone="error"
          title="This member’s sets could not be loaded."
          actions={
            <Button size="sm" color="secondary" onClick={() => void refresh()}>
              Try again
            </Button>
          }
        >
          <FailureText raw={load.detail} />
        </Callout>
      ) : !load.sets.length ? (
        <Callout tone="neutral" title="This member has no sets.">
          Nothing detects sets yet, so a member only has the sets made for them here.
        </Callout>
      ) : (
        <ul className="flex flex-col gap-4" aria-label="Sets">
          {load.sets.map((set) => (
            <GarmentSetCard
              key={set.set_id}
              memberId={memberId}
              set={set}
              onChanged={() => void refresh()}
            />
          ))}
        </ul>
      )}
    </Section>
  );
}

function GarmentSetCard({
  memberId,
  set,
  onChanged,
}: {
  memberId: string;
  set: AdminGarmentSet;
  onChanged: () => void;
}) {
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-secondary p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-md font-semibold text-primary">{set.name}</h4>
          <Status value={set.lifecycle_status} />
          {set.lifecycle_status === 'active' && !set.visible_to_member ? (
            <Status value="hidden" />
          ) : null}
        </div>
        {set.lifecycle_status === 'active' ? (
          <RetireGarmentSet memberId={memberId} set={set} onDone={onChanged} />
        ) : null}
      </div>
      <DetailList
        items={[
          ['Member’s answer', APPROVAL_WORDS[set.approval_status]],
          ['Made by', madeBy(set)],
          ['Made', when(set.created_at)],
          ['Answered', when(set.reviewed_at)],
          ...(set.retired_at
            ? ([['Retired', when(set.retired_at)]] as Array<[string, ReactNode]>)
            : []),
        ]}
      />
      {set.lifecycle_status === 'active' && !set.visible_to_member ? (
        <p className="text-sm text-tertiary">
          The member can’t see this set: they rejected it, fewer than two of its garments are still
          in the wardrobe, or a required garment is archived.
        </p>
      ) : null}
      <ul className="flex flex-col gap-1 text-sm" aria-label={`Garments in ${set.name}`}>
        {set.items.map((item) => (
          <li key={item.garment_id} className="flex flex-wrap items-center gap-2">
            <EntityRef
              kind="garment"
              id={item.garment_id}
              memberId={memberId}
              label={item.label}
              hideId
            />
            <span className="text-tertiary">
              as {item.role}, {item.required ? 'required' : 'optional'}
            </span>
            {item.garment_status !== 'active' ? <Status value={item.garment_status} /> : null}
          </li>
        ))}
      </ul>
      <details className="text-sm text-tertiary">
        <summary className="cursor-pointer">Record details</summary>
        <div className="flex flex-col gap-2 pt-2">
          <IdentifierValue label="Set id" value={set.set_id} />
          {set.created_by_id ? (
            <span className="flex flex-wrap items-center gap-2">
              Made by operator
              <EntityRef kind="staff" id={set.created_by_id} />
            </span>
          ) : null}
          {set.confidence !== null ? (
            <span>Detector confidence: {Math.round(set.confidence * 100)}%</span>
          ) : null}
          <span>Revision {formatCount(set.revision)}</span>
          {Object.keys(set.provenance).length ? (
            <pre className="whitespace-pre-wrap break-words">
              {JSON.stringify(set.provenance, null, 2)}
            </pre>
          ) : null}
        </div>
      </details>
    </li>
  );
}

function RetireGarmentSet({
  memberId,
  set,
  onDone,
}: {
  memberId: string;
  set: AdminGarmentSet;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();

  const run = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await retireMemberGarmentSetApiV1InternalUsersIdGarmentSetsSetIdDelete({
      path: { id: memberId, set_id: set.set_id },
      body: { expected_revision: set.revision, reason: reason.trim() },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot change this member’s sets.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That set could not be retired.'));
      return;
    }
    setOpen(false);
    onDone();
  };

  return (
    <>
      <Button
        size="sm"
        color="secondary"
        onClick={() => {
          setReason('');
          setFailure(undefined);
          setOpen(true);
        }}
      >
        Retire set
      </Button>
      {open ? (
        <ActionDialog
          title={`Retire “${set.name}”`}
          description="The member stops seeing this set, and its garments can be made into a set again. Their answer to it, if they gave one, stays on record. The change is recorded against your operator account."
          onClose={() => setOpen(false)}
        >
          <div className="flex flex-col gap-4">
            {failure ? <FailureText raw={failure} /> : null}
            <TextAreaField
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <Actions>
              <Button size="sm" color="secondary" isDisabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                color="primary-destructive"
                isDisabled={reason.trim().length < 3 || busy}
                onClick={() => void run()}
              >
                {busy ? 'Working…' : 'Confirm retire set'}
              </Button>
            </Actions>
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}

/** "Make a set" for the garments ticked in the table. */
export function MakeGarmentSet({
  memberId,
  garmentIds,
  onMade,
}: {
  memberId: string;
  garmentIds: readonly string[];
  onMade: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [made, setMade] = useState<{ set: AdminGarmentSet; created: boolean }>();
  const fits = garmentIds.length >= MIN_SET_PIECES && garmentIds.length <= MAX_SET_PIECES;

  const run = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await createMemberGarmentSetApiV1InternalUsersIdGarmentSetsPost({
      path: { id: memberId },
      body: {
        items: garmentIds.map((garment_id) => ({ garment_id })),
        name: name.trim(),
        reason: reason.trim(),
      },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot change this member’s sets.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That set could not be made.'));
      return;
    }
    setMade(result.data);
    onMade();
  };

  return (
    <>
      <Button
        size="sm"
        color="secondary"
        isDisabled={!fits}
        onClick={() => {
          setName('');
          setReason('');
          setFailure(undefined);
          setMade(undefined);
          setOpen(true);
        }}
      >
        Make a set
      </Button>
      {open ? (
        <ActionDialog
          title={`Make a set of ${formatCount(garmentIds.length)} garments`}
          description={`${SET_DEFINITION} The member is asked in the app whether it is a set they wear together.`}
          onClose={() => setOpen(false)}
        >
          <div className="flex flex-col gap-4">
            {failure ? <FailureText raw={failure} /> : null}
            {made ? (
              <Callout
                tone={made.created ? 'success' : 'neutral'}
                title={
                  made.created
                    ? `“${made.set.name}” was made.`
                    : `These garments are already the set “${made.set.name}”.`
                }
              >
                {made.created ? 'It now waits for the member’s answer.' : 'Nothing was changed.'}
              </Callout>
            ) : (
              <>
                <TextField
                  label="Set name"
                  hint="What the member sees, for example “Ivory kurta and dhoti”."
                  value={name}
                  maxLength={120}
                  onChange={(event) => setName(event.target.value)}
                />
                <TextAreaField
                  label="Reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </>
            )}
            <Actions>
              <Button size="sm" color="secondary" isDisabled={busy} onClick={() => setOpen(false)}>
                {made ? 'Close' : 'Cancel'}
              </Button>
              {made ? null : (
                <Button
                  size="sm"
                  color="primary"
                  isDisabled={!name.trim() || reason.trim().length < 3 || busy}
                  onClick={() => void run()}
                >
                  {busy ? 'Working…' : 'Confirm make set'}
                </Button>
              )}
            </Actions>
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}
